import { Button, Divider, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { createReference } from '@medplum/core';
import type { Coverage, Organization, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { withTenantScope } from '../../utils/tenant';
import { SettingsSection } from './SettingsSection';
import {
  friendlyErrorMessage,
  getCoverageClassName,
  getCoverageClassValue,
  getCoveragePhone,
  setCoverageClass,
  setCoveragePhone,
  stripSurroundingQuotes,
} from './settings.utils';

const SELF_PAY_OPTION = 'Self-pay';
const DEFAULT_INSURANCE_OPTIONS = [
  'CareFirst BlueCross BlueShield District of Columbia',
  'CareFirst Blue Cross Blue Shield Maryland',
  'CareFirst BlueCross BlueShield Medicare Advantage',
  'Other',
];

interface InsuranceFormData {
  coverageOrder: string;
  insuranceType: string;
  planName: string;
  memberId: string;
  groupId: string;
  phone: string;
}

function getCoverageOrder(coverage: Coverage | undefined, fallback: number): number {
  return coverage?.order ?? fallback;
}

function buildFormData(coverage: Coverage | undefined, payorName: string, fallbackOrder: number): InsuranceFormData {
  return {
    coverageOrder: String(getCoverageOrder(coverage, fallbackOrder)),
    insuranceType: coverage ? payorName || coverage.payor?.[0]?.display || '' : '',
    planName: coverage ? getCoverageClassName(coverage, 'plan') : '',
    memberId: coverage ? stripSurroundingQuotes(coverage.subscriberId ?? '') : '',
    groupId: coverage ? getCoverageClassValue(coverage, 'group') : '',
    phone: coverage ? getCoveragePhone(coverage) : '',
  };
}

function sortCoverages(coverages: Coverage[]): Coverage[] {
  return [...coverages].sort((a, b) => getCoverageOrder(a, 99) - getCoverageOrder(b, 99));
}

interface InsuranceSectionProps {
  patient: Patient;
  coverages: Coverage[];
  payorNames: Record<string, string>;
  insuranceOptions?: string[];
  onCoveragesUpdated: (coverages: Coverage[]) => void;
}

export function InsuranceSection({
  patient,
  coverages,
  payorNames,
  insuranceOptions = DEFAULT_INSURANCE_OPTIONS,
  onCoveragesUpdated,
}: InsuranceSectionProps): JSX.Element {
  const sortedCoverages = useMemo(
    () => sortCoverages(coverages.filter((coverage) => coverage.status === 'active')),
    [coverages]
  );
  const [modalCoverage, setModalCoverage] = useState<Coverage | undefined>();
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = useCallback((coverage?: Coverage) => {
    setModalCoverage(coverage);
    setModalOpen(true);
  }, []);

  const upsertCoverage = useCallback(
    (saved: Coverage) => {
      const next = saved.status === 'active' ? saved : undefined;
      const withoutSaved = sortedCoverages.filter((coverage) => coverage.id !== saved.id);
      onCoveragesUpdated(sortCoverages(next ? [...withoutSaved, next] : withoutSaved));
    },
    [onCoveragesUpdated, sortedCoverages]
  );

  const fallbackOrder = sortedCoverages.some((coverage) => getCoverageOrder(coverage, 1) === 1) ? 2 : 1;
  const titleForOrder = (order: number): string => (order === 2 ? 'Secondary Insurance' : 'Primary Insurance');

  return (
    <>
      <SettingsSection
        title="Insurance"
        editing={false}
        onEdit={() => openModal()}
        onSave={() => undefined}
        onCancel={() => undefined}
        saving={false}
        actions={
          sortedCoverages.length < 2 ? (
            <Button size="xs" onClick={() => openModal()}>
              Add
            </Button>
          ) : null
        }
      >
        {sortedCoverages.length === 0 ? (
          <Text c="dimmed">No insurance information on file.</Text>
        ) : (
          <Stack gap="lg">
            {sortedCoverages.map((coverage, index) => {
              const order = getCoverageOrder(coverage, index + 1);
              const payorName = payorNames[coverage.id ?? ''] ?? coverage.payor?.[0]?.display ?? '';
              return (
                <Stack key={coverage.id ?? `${coverage.resourceType}-${index}`} gap="sm">
                  {index > 0 && <Divider />}
                  <Group justify="space-between">
                    <Title order={4}>{titleForOrder(order)}</Title>
                    <Button variant="subtle" size="xs" onClick={() => openModal(coverage)}>
                      Edit
                    </Button>
                  </Group>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                    <FieldDisplay label="Plan name" value={getCoverageClassName(coverage, 'plan')} />
                    <FieldDisplay label="Plan carrier" value={payorName} />
                    <FieldDisplay label="Member ID" value={stripSurroundingQuotes(coverage.subscriberId ?? '')} />
                    <FieldDisplay label="Group ID" value={getCoverageClassValue(coverage, 'group')} />
                    <FieldDisplay label="Phone" value={getCoveragePhone(coverage)} />
                  </SimpleGrid>
                </Stack>
              );
            })}
          </Stack>
        )}
      </SettingsSection>

      <InsuranceModal
        opened={modalOpen}
        patient={patient}
        coverage={modalCoverage}
        fallbackOrder={fallbackOrder}
        payorName={modalCoverage ? (payorNames[modalCoverage.id ?? ''] ?? '') : ''}
        insuranceOptions={insuranceOptions}
        onClose={() => setModalOpen(false)}
        onSaved={(saved) => {
          upsertCoverage(saved);
          setModalOpen(false);
        }}
        onSelfPay={() => {
          setModalOpen(false);
        }}
      />
    </>
  );
}

interface InsuranceModalProps {
  opened: boolean;
  patient: Patient;
  coverage: Coverage | undefined;
  fallbackOrder: number;
  payorName: string;
  insuranceOptions: string[];
  onClose: () => void;
  onSaved: (coverage: Coverage) => void;
  onSelfPay: () => void;
}

function InsuranceModal({
  opened,
  patient,
  coverage,
  fallbackOrder,
  payorName,
  insuranceOptions,
  onClose,
  onSaved,
  onSelfPay,
}: InsuranceModalProps): JSX.Element {
  const medplum = useMedplum();
  const [saving, setSaving] = useState(false);
  const form = useForm<InsuranceFormData>({
    initialValues: buildFormData(coverage, payorName, fallbackOrder),
    validate: {
      insuranceType: (value) => (value ? null : 'Insurance type is required'),
      memberId: (value, values) =>
        values.insuranceType && values.insuranceType !== SELF_PAY_OPTION && !value.trim()
          ? 'Member ID is required'
          : null,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the modal form only when the modal target changes
  useEffect(() => {
    if (opened) {
      const next = buildFormData(coverage, payorName, fallbackOrder);
      form.setValues(next);
      form.resetDirty(next);
      form.clearErrors();
    }
  }, [coverage, fallbackOrder, opened, payorName]);

  const handleSubmit = form.onSubmit(async (values) => {
    setSaving(true);
    try {
      await Sentry.startSpan({ op: 'http.client', name: coverage ? 'Edit Insurance' : 'Add Insurance' }, async () => {
        if (values.insuranceType === SELF_PAY_OPTION) {
          if (coverage) {
            const saved = await medplum.updateResource<Coverage>({ ...coverage, status: 'cancelled' });
            onSaved(saved);
          } else {
            onSelfPay();
          }
          showNotification({ color: 'green', message: 'Insurance set to self-pay.' });
          return;
        }

        let payor: Organization | undefined;
        if (values.insuranceType !== 'Other') {
          payor = await medplum.searchOne('Organization', { name: values.insuranceType });
        }

        let updated: Coverage = coverage
          ? { ...coverage }
          : withTenantScope({
              resourceType: 'Coverage',
              status: 'active',
              beneficiary: { reference: `Patient/${patient.id}` },
              subscriber: { reference: `Patient/${patient.id}` },
              relationship: {
                coding: [
                  {
                    system: 'http://terminology.hl7.org/CodeSystem/subscriber-relationship',
                    code: 'self',
                    display: 'Self',
                  },
                ],
              },
              payor: [],
            });

        updated.status = 'active';
        updated.order = Number(values.coverageOrder);
        updated.subscriberId = values.memberId.trim() || undefined;
        updated = setCoverageClass(updated, 'plan', values.planName.trim(), values.planName.trim());
        updated = setCoverageClass(updated, 'group', values.groupId.trim());
        updated = setCoveragePhone(updated, values.phone.trim());
        updated.payor = payor?.id
          ? [{ ...createReference(payor), display: payor.name }]
          : [{ display: values.insuranceType === 'Other' ? 'Other' : values.insuranceType }];

        const saved = coverage
          ? await medplum.updateResource(updated)
          : await medplum.createResource<Coverage>(updated);
        onSaved(saved);
        showNotification({ color: 'green', message: 'Insurance information updated.' });
      });
    } catch (error) {
      Sentry.captureException(error);
      showNotification({ color: 'red', message: friendlyErrorMessage(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  });

  const handleRemove = async (): Promise<void> => {
    if (!coverage) {
      return;
    }
    setSaving(true);
    try {
      await Sentry.startSpan({ op: 'http.client', name: 'Remove Insurance' }, async () => {
        const saved = await medplum.updateResource<Coverage>({ ...coverage, status: 'cancelled' });
        onSaved(saved);
      });
      showNotification({ color: 'green', message: 'Insurance removed.' });
    } catch (error) {
      Sentry.captureException(error);
      showNotification({ color: 'red', message: friendlyErrorMessage(error), autoClose: false });
    } finally {
      setSaving(false);
    }
  };

  const showInsuranceFields = form.values.insuranceType && form.values.insuranceType !== SELF_PAY_OPTION;

  return (
    <Modal opened={opened} onClose={onClose} title={coverage ? 'Edit insurance' : 'Add insurance'} size="lg">
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="md">
          <Select
            label="Coverage type"
            data={[
              { value: '1', label: 'Primary insurance' },
              { value: '2', label: 'Secondary insurance' },
            ]}
            withAsterisk
            {...form.getInputProps('coverageOrder')}
          />
          <Select
            label="Insurance type"
            data={[SELF_PAY_OPTION, ...insuranceOptions]}
            withAsterisk
            searchable
            {...form.getInputProps('insuranceType')}
          />
          {form.values.insuranceType === SELF_PAY_OPTION && (
            <Text size="sm" c="dimmed">
              Self-pay is treated as no insurance provided.
            </Text>
          )}
          {showInsuranceFields && (
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput label="Plan name" {...form.getInputProps('planName')} />
              <TextInput label="Member ID" withAsterisk {...form.getInputProps('memberId')} />
              <TextInput label="Group ID" {...form.getInputProps('groupId')} />
              <TextInput label="Phone" type="tel" {...form.getInputProps('phone')} />
            </SimpleGrid>
          )}
          <Group justify="space-between">
            {coverage ? (
              <Button color="red" variant="subtle" onClick={() => handleRemove()} disabled={saving}>
                Remove insurance
              </Button>
            ) : (
              <div />
            )}
            <Group gap="xs">
              <Button variant="default" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" loading={saving}>
                Save
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function FieldDisplay({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </div>
  );
}
